import React from 'react';
import { Badge, Icon } from '@openedx/paragon';
import { RiTimerLine, RiStarFill, RiLockLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';

import messages from '../messages';

const MAX_VISIBLE_SKILLS = 3;

const CourseStatusBadge = ({ status }) => {
  const intl = useIntl();
  if (status === 'completed') {
    return (
      <Badge variant="soft" color="success" className="program-inner__course-status">
        {intl.formatMessage(messages.statusCompleted)}
      </Badge>
    );
  }
  if (status === 'in-progress') {
    return (
      <Badge variant="soft" color="gray" className="program-inner__course-status">
        {intl.formatMessage(messages.statusInProgress)}
      </Badge>
    );
  }
  return null;
};

const ProgramCourseRow = ({ course, onLockedClick }) => {
  const intl = useIntl();
  const {
    title, imageUrl, duration, rating, reviewCount, skills, status, homeUrl, isLocked,
  } = course;

  const visibleSkills = skills.slice(0, MAX_VISIBLE_SKILLS);
  const extraSkills = skills.length - visibleSkills.length;
  const hasMeta = Boolean(duration) || rating != null;

  const body = (
    <>
      <div
        className="program-inner__course-image"
        style={{ backgroundImage: `url(${imageUrl})` }}
        role="img"
        aria-label={title}
      >
        {isLocked && (
          <div className="program-inner__course-lock">
            <Icon src={RiLockLine} size="inline" />
          </div>
        )}
      </div>
      <div className="program-inner__course-info">
        <span className="program-inner__course-title" title={title}>{title}</span>
        {hasMeta && (
          <div className="program-inner__course-meta">
            {duration && (
              <span className="program-inner__course-meta-item">
                <Icon src={RiTimerLine} size="xs" />
                {duration}
              </span>
            )}
            {duration && rating != null && (
              <span className="program-inner__course-meta-dot" aria-hidden />
            )}
            {rating != null && (
              <span className="program-inner__course-meta-item">
                <Icon src={RiStarFill} size="xs" />
                {intl.formatMessage(messages.reviews, {
                  rating,
                  count: reviewCount.toLocaleString(),
                })}
              </span>
            )}
          </div>
        )}
        {skills.length > 0 && (
          <div className="program-inner__course-skills">
            {visibleSkills.map((skill) => (
              <Badge key={skill} variant="soft" color="gray" pill>
                {skill}
              </Badge>
            ))}
            {extraSkills > 0 && (
              <Badge variant="soft" color="gray" pill>
                {intl.formatMessage(messages.moreSkills, { count: extraSkills })}
              </Badge>
            )}
          </div>
        )}
      </div>
      <CourseStatusBadge status={status} />
    </>
  );

  if (isLocked) {
    return (
      <button
        type="button"
        className="program-inner__course-row program-inner__course-row--locked"
        aria-label={title}
        onClick={() => onLockedClick?.(course)}
      >
        {body}
      </button>
    );
  }

  return (
    <a className="program-inner__course-row" href={homeUrl}>
      {body}
    </a>
  );
};

const EnrolledProgramCourses = ({ courses, onLockedClick }) => {
  const intl = useIntl();
  if (!courses || courses.length === 0) return null;

  return (
    <section className="program-inner__card program-inner__courses">
      <h2 className="program-inner__card-title">
        {intl.formatMessage(messages.coursesTitle)}
      </h2>
      <div className="program-inner__courses-list">
        {courses.map((course) => (
          <ProgramCourseRow
            key={course.contentId}
            course={course}
            onLockedClick={onLockedClick}
          />
        ))}
      </div>
    </section>
  );
};

export default EnrolledProgramCourses;
